import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  model,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Country, Installer, Language, UsersService } from '@ubie/core';
import { FeedbackService, FormHelper } from '@ubie/shared';
import { AccountRegistrationSliderEvent } from '../../../interfaces/account-registration-slider-event.interface';

@Component({
  selector: 'acl-register-professional-details',
  standalone: false,
  templateUrl: './register-professional-details.component.html',
  styleUrl: './register-professional-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterProfessionalDetailsComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UsersService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly slideNo = input<number>(2);
  readonly installer = model.required<Installer>();

  @Output() readonly slideTo =
    new EventEmitter<AccountRegistrationSliderEvent>();

  readonly professionalDetailsForm = this.formBuilder.group({
    companyname: [''],
    vatno: [''],
    streetNr: ['', Validators.required],
    postalCode: ['', Validators.required],
    city: ['', Validators.required],
    country: ['', Validators.required],
    language: [0, Validators.required],
    terms: [false, Validators.requiredTrue], // requiredTrue ensures the box must be checked
  });

  readonly formHelper = new FormHelper(this.professionalDetailsForm);
  readonly isFormSubmitAttempted = signal(false);

  readonly countriesData = signal<Country[]>([]);
  readonly languageData = signal<Language[]>([]);

  ngOnInit(): void {
    this.countriesData.set(this.activatedRoute.snapshot?.data['countries']);
    this.languageData.set(this.activatedRoute.snapshot?.data['languages']);
  }

  submitDetails(): void {
    this.isFormSubmitAttempted.set(true);
    if (this.professionalDetailsForm.valid) {
      const selectedLangId =
        this.professionalDetailsForm.get('language')?.value;

      const selectedLang = this.languageData().find(
        (lang) => lang.id === selectedLangId,
      );

      this.installer.update((installer) => {
        return {
          ...installer,
          streetNr: this.professionalDetailsForm.get('streetNr')?.value ?? '',
          postalCode:
            this.professionalDetailsForm.get('postalCode')?.value ?? '',
          city: this.professionalDetailsForm.get('city')?.value ?? '',
          country: this.professionalDetailsForm.get('country')?.value ?? '',
          language: selectedLang ?? installer.language,
          companyname:
            this.professionalDetailsForm.get('companyname')?.value ?? '',
          vatno: this.professionalDetailsForm.get('vatno')?.value ?? '',
        };
      });

      this.userService.createInstallerUser(this.installer()).subscribe({
        next: () => {
          this.slideTo.emit({ slideNo: 3, user: this.installer() });
        },
        error: (error) => {
          this.feedbackService.present(error);
        },
      });
    }
  }

  goBack(): void {
    this.slideTo.emit({ slideNo: 3, user: this.installer() });
  }
}
